import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Building2, Globe, Mail, Phone, MapPin, ChevronRight } from 'lucide-react';
import { useKB } from './KBContext';
import { MemberAvatar } from './MemberSheet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABEL = { board: 'Boards', committee: 'Committees', staff: 'Staff', chapter: 'Chapters' };
const CATEGORY_SORT = { staff: 0, committee: 1, board: 2, chapter: 3 };

const PersonRow = ({ person, onMemberClick }) => {
  const clickable = person.is_member && person.member_id;
  const Wrapper = clickable ? 'button' : 'div';
  return (
    <Wrapper
      type={clickable ? 'button' : undefined}
      onClick={clickable ? () => onMemberClick(person.member_id) : undefined}
      data-testid={`kb-org-person${clickable ? '-linked' : ''}`}
      className="kb-org-person"
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
        padding: '10px 12px', borderRadius: 10, border: '1px solid var(--kb-border)',
        background: 'var(--kb-bg-soft, rgba(255,255,255,0.02))',
        cursor: clickable ? 'pointer' : 'default', transition: 'all 0.16s ease',
      }}
      onMouseEnter={clickable ? (e) => { e.currentTarget.style.borderColor = 'rgba(91,154,255,0.45)'; } : undefined}
      onMouseLeave={clickable ? (e) => { e.currentTarget.style.borderColor = ''; } : undefined}
    >
      <MemberAvatar member={{ full_name: person.name, photo_url: person.photo_url }} size={40} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--kb-fg)' }}>{person.name}</div>
        {person.company && (
          <div style={{ fontSize: 12.5, color: 'var(--kb-fg-soft, #8B96A5)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {person.company}
          </div>
        )}
        {!clickable && person.email && (
          <a href={`mailto:${person.email}`} data-testid="kb-org-person-email"
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: 12, color: 'var(--kb-accent, #2563EB)', textDecoration: 'none' }}>
            {person.email}
          </a>
        )}
      </div>
      {person.role && (
        <span style={{
          flexShrink: 0, padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
          background: 'rgba(37,99,235,0.10)', border: '1px solid rgba(37,99,235,0.28)',
          color: 'var(--kb-accent, #2563EB)', letterSpacing: '0.01em',
        }}>{person.role}</span>
      )}
      {clickable && <ChevronRight size={15} style={{ flexShrink: 0, color: 'var(--kb-fg-soft, #8B96A5)' }} />}
    </Wrapper>
  );
};

const OrgSheet = ({ onClose, onMemberClick }) => {
  const { password } = useKB();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/kb/org/ipmi`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [password]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const org = data?.org || {};
  const groups = data?.groups || [];
  // Section by category
  const cats = [...new Set(groups.map((g) => g.category))].sort(
    (a, b) => (CATEGORY_SORT[a] ?? 9) - (CATEGORY_SORT[b] ?? 9)
  );

  return (
    <AnimatePresence>
      <motion.div
        key="org-sheet-backdrop"
        className="kb-member-sheet-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }} onClick={onClose}
        data-testid="kb-org-sheet-backdrop"
      />
      <motion.aside
        key="org-sheet-panel"
        className="kb-member-sheet"
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
        data-testid="kb-org-sheet"
        style={{ maxWidth: 560, width: '100%' }}
      >
        <button className="kb-member-sheet-close" onClick={onClose} aria-label="Close" data-testid="kb-org-sheet-close">
          <X size={18} />
        </button>

        {loading ? (
          <div className="kb-members-loading"><Loader2 className="spin" size={20} /> Loading…</div>
        ) : (
          <div style={{ overflowY: 'auto', height: '100%', paddingBottom: 40 }}>
            {/* Org header */}
            <div className="kb-member-sheet-hero" style={{ paddingBottom: 12 }}>
              <div style={{
                width: 96, height: 96, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: org.logo_url ? '#fff' : 'var(--kb-accent, #2563EB)',
                border: org.logo_url ? '1px solid var(--kb-border)' : 'none',
                color: '#fff', padding: org.logo_url ? 12 : 0, overflow: 'hidden',
              }}>
                {org.logo_url ? (
                  <img src={org.logo_url} alt={org.short_name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <Building2 size={38} />
                )}
              </div>
              <h2 className="kb-member-sheet-name" style={{ marginTop: 14 }}>{org.name}</h2>
              {org.tagline && <div className="kb-member-sheet-position">{org.tagline}</div>}
              {org.founded && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--kb-fg-soft, #8B96A5)' }}>
                  Established {org.founded}
                </div>
              )}
            </div>

            <div style={{ padding: '4px 24px' }}>
              {org.description && (
                <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--kb-fg)', margin: '0 0 16px' }}>
                  {org.description}
                </p>
              )}

              {/* Contact chips */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
                {org.website && (
                  <a href={org.website} target="_blank" rel="noopener noreferrer" data-testid="kb-org-website"
                    style={contactChip}><Globe size={13} /> Website</a>
                )}
                {org.email && (
                  <a href={`mailto:${org.email}`} data-testid="kb-org-email" style={contactChip}><Mail size={13} /> {org.email}</a>
                )}
                {org.phone && (
                  <a href={`tel:${org.phone.replace(/[^+0-9]/g, '')}`} style={contactChip}><Phone size={13} /> {org.phone}</a>
                )}
                {org.address && (
                  <span style={{ ...contactChip, cursor: 'default' }}><MapPin size={13} /> {org.address}</span>
                )}
              </div>

              {/* Governance sections */}
              {cats.map((cat) => (
                <div key={cat} style={{ marginBottom: 28 }} data-testid={`kb-org-section-${cat}`}>
                  <div style={{
                    fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: 'var(--kb-fg-soft, #8B96A5)', marginBottom: 12,
                    borderBottom: '1px solid var(--kb-border)', paddingBottom: 8,
                  }}>
                    {CATEGORY_LABEL[cat] || cat}
                  </div>
                  {groups.filter((g) => g.category === cat).map((g) => (
                    <div key={g.slug} style={{ marginBottom: 20 }}>
                      <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: 'var(--kb-fg)' }}>
                        {g.name}
                      </h3>
                      {g.description && (
                        <p style={{ margin: '0 0 12px', fontSize: 12.5, lineHeight: 1.5, color: 'var(--kb-fg-soft, #8B96A5)' }}>
                          {g.description}
                        </p>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {g.members.map((p, i) => (
                          <PersonRow key={`${g.slug}-${i}`} person={p} onMemberClick={onMemberClick} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.aside>
    </AnimatePresence>
  );
};

const contactChip = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999,
  fontSize: 12.5, fontWeight: 500, textDecoration: 'none',
  background: 'var(--kb-bg-soft, rgba(255,255,255,0.03))', border: '1px solid var(--kb-border)',
  color: 'var(--kb-fg)',
};

export default OrgSheet;
